import React, { useEffect, useState } from "react";
import { Grid, Box } from "@mui/material";
import styled from "@emotion/styled";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { getClient, getUnbilledCharges } from "../redux/ducks/client";
import { getInvoicesByBillingEntities } from "../redux/ducks/invoices";
import ClientInfoGridBox from "../components/ClientInfoGridBox";
import DeactivatedAlert from "../components/DeactivatedAlert";
import RetainerOverageNotice from "../components/RetainerOverageNotice";
import InfoSmallBox from "../components/InfoSmallBox";
import ClientInfoUserCard from "../components/ClientInfoUserCard";
import ClientAccountList from "../components/ClientAccountList";
import ClientDocumentList from "../components/ClientDocumentList";
import ClientHoursByAccountBox from "../components/ClientHoursByAccountBox";
import PageHeader from "../components/PageHeader";
import { useHasPermission } from "../utils/permissionUtils";
import { getCurrentMonth, getLastMonth } from "../utils/dateUtils";
import { convertMinutesToHoursSorted } from "../utils/timeUtils";

const ContainerBox = styled(Box)(({ theme }) => ({
    width: "100%",
    minWidth: 0,
    maxWidth: theme.breakpoints.values.lg,
    marginRight: "auto",
    marginLeft: "auto",
    padding: theme.spacing(2),
    overflowX: "clip",
    [theme.breakpoints.up("md")]: {
        padding: theme.spacing(3),
    },
    [theme.breakpoints.up("lg")]: {
        padding: theme.spacing(4),
    },
}));

const Client = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { clientSlug } = useParams();
    const [isLoading, setIsLoading] = useState(true);
    const refreshTrigger = useSelector((state) => state.refreshTriggers.client);
    const [clientId, setClientId] = useState(null);
    const [clientData, setClientData] = useState({ address: {}, billingTerms: {}, accounts: [] });
    const [invoices, setInvoices] = useState([]);
    const [unbilledCharges, setUnbilledCharges] = useState(undefined);
    const [accountsWithTime, setAccountsWithTime] = useState([]);
    const [invoicesLoading, setInvoicesLoading] = useState(true);
    const hasPermission = useHasPermission();
    const canSeeInternal = hasPermission("canSeeInternal");
    const canManageOwnClient = hasPermission("manageOwnClient");

    useEffect(() => {
        const handleClientData = (data) => {
            if (!data) {
                setIsLoading(false);
                return;
            }
            setClientData(data);
            setClientId(data._id);
            setAccountsWithTime(Array.isArray(data.accountsTime) ? data.accountsTime : []);
            setIsLoading(false);

            if (data._id) {
                if (!canSeeInternal) {
                    fetchInvoices(data._id);
                    fetchUnbilledCharges(data._id);
                }
            }
        };

        setIsLoading(true);
        dispatch(getClient(clientSlug, handleClientData));
    }, [clientSlug, dispatch, refreshTrigger]);

    const fetchInvoices = (id) => {
        setInvoicesLoading(true);
        dispatch(getInvoicesByBillingEntities((invoicesData) => {
            setInvoicesLoading(false);
            if (invoicesData && Array.isArray(invoicesData)) {
                // Filter invoices by clientId
                const clientInvoices = invoicesData.filter(inv => {
                    const invClientId = inv.client?._id?.toString() || inv.client?.toString();
                    return invClientId === id.toString();
                });
                setInvoices(clientInvoices);
            } else {
                setInvoices([]);
            }
        }));
    };

    const formatUnbilledCharges = (data) => {
        if (data === undefined) return "Checking…";
        if (!data) return "Unavailable";
        if (data.total === null || data.total === undefined) return "$ --.--";
        return `$ ${data.total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    const fetchUnbilledCharges = (id) => {
        dispatch(getUnbilledCharges(id, (data) => {
            setUnbilledCharges(data || null);
        }));
    };

    const openProjects = {
        title: "Open Projects",
        content: clientData.openProjects,
    };

    // Calculate invoice summary
    const calculateInvoiceSummary = () => {
        if (invoicesLoading || !invoices || invoices.length === 0) {
            return {
                title: "Invoices",
                content: {
                    "Outstanding Balance": "$ 0.00",
                    "Unpaid Invoices": [],
                    "Unbilled Charges": formatUnbilledCharges(unbilledCharges),
                },
            };
        }

        const unpaidInvoices = invoices.filter(inv =>
            inv.status === 'Open' || inv.status === 'Overdue'
        );
        const outstandingBalance = unpaidInvoices.reduce((sum, inv) => {
            const paymentsTotal = Array.isArray(inv.payments)
                ? inv.payments.reduce((pSum, p) => pSum + (p.amount || 0), 0)
                : 0;
            return sum + ((inv.amount || 0) - paymentsTotal);
        }, 0);
        
        // Get list of unpaid invoice numbers with their IDs for linking
        const unpaidInvoiceData = unpaidInvoices
            .filter(inv => inv.invoiceNumber)
            .map(inv => ({ number: inv.invoiceNumber, id: inv._id }));

        return {
            title: "Invoices",
            content: {
                "Outstanding Balance": `$ ${outstandingBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                "Unpaid Invoices": unpaidInvoiceData.length > 0 ? unpaidInvoiceData : [],
                "Unbilled Charges": formatUnbilledCharges(unbilledCharges),
            },
            invoiceData: unpaidInvoiceData, // Pass invoice data for clickable links
            onClick: () => {
                // Navigate to first unpaid invoice if available
                if (unpaidInvoiceData.length > 0) {
                    navigate(`/client/${clientSlug}/invoices/${unpaidInvoiceData[0].id}`);
                }
            }
        };
    };

    const openInvoices = calculateInvoiceSummary();

    const openTasks = {
        title: "Open Tasks",
        content: clientData.openTasks,
    };

    let lastMonthTime = {};
    let thisMonthTime = {};

    if (clientData.aggregatedTime !== undefined) {
        lastMonthTime = {
            title: "Previous Month",
            content: convertMinutesToHoursSorted(clientData.aggregatedTime.monthly[getLastMonth()] || {Hourly: 0}),
        };

        thisMonthTime = {
            title: "Current Month",
            content: convertMinutesToHoursSorted(clientData.aggregatedTime.monthly[getCurrentMonth()] || {Hourly: 0}),
        };
    }

    return (
        <ContainerBox aria-label="ContainerBox">
            <PageHeader title={clientData?.name || "Client"} />
            {clientData.active === false && (
                <DeactivatedAlert entityType="client" />
            )}
            <RetainerOverageNotice status={clientData?.retainerStatus} />
            <Grid container spacing={{ xs: 3, md: 4, lg: 5 }}>
                <Grid item xs={12} md={6}>
                    <ClientInfoGridBox clientData={clientData} />
                </Grid>
                <Grid item xs={12} md={6}>
                    <Grid container spacing={{ xs: 3, md: 4, lg: 5 }} item xs={12} md={6}>
                        <InfoSmallBox {...openProjects} />
                        {canSeeInternal ? <InfoSmallBox {...openTasks} /> : <InfoSmallBox {...openInvoices} />}
                        <InfoSmallBox {...lastMonthTime} />
                        <InfoSmallBox {...thisMonthTime} />
                    </Grid>
                </Grid>

                {clientData.accounts && (
                    <>
                        <Grid item xs={12} md={6}>
                            <ClientHoursByAccountBox
                                accountsWithTime={accountsWithTime}
                                loading={isLoading}
                                accounts={clientData.accounts}
                                month={getCurrentMonth()}
                                title="Monthly Time - Current"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <ClientHoursByAccountBox
                                accountsWithTime={accountsWithTime}
                                loading={isLoading}
                                accounts={clientData.accounts}
                                month={getLastMonth()}
                                title="Monthly Time - Previous"
                            />
                        </Grid>
                    </>
                )}

                <Grid item xs={12} md={6} >
                    <ClientAccountList accountsNumber={clientData.accountsNumber} accounts={clientData.accounts} clientId={clientId} users={clientData.users} client={clientData} />
                    {(canSeeInternal || canManageOwnClient) && (
                        <ClientDocumentList
                            documents={clientData.signedDocuments || []}
                            clientId={clientId}
                            client={clientData}
                        />
                    )}
                </Grid>

                <Grid item xs={12} md={6}>
                    <ClientInfoUserCard users={clientData.users} isLoading={isLoading} clientId={clientId} isInternal={clientData.isInternal} client={clientData} />
                </Grid>
            </Grid>
        </ContainerBox>
    );
};

export default Client;
